import { useGeneralConfig } from "@/hooks/config/useGeneralConfig";
import { useNodegroupConfig } from "@/hooks/config/useNodegroupConfig";
import { useHadoopConfig } from "@/hooks/config/useHadoopConfig";
import { useMySQLConfig } from "@/hooks/config/useMySQLConfig";
import { useVIAConfig } from "@/hooks/config/useVIAConfig";
import { useAIOpsConfig } from "@/hooks/config/useAIOpsConfig";
import { usePoolConfig } from "@/hooks/config/usePoolConfig";
import { usePoolGeneralConfig } from "./usePoolGeneralConfig";
import { useNodegroupGeneralConfig } from "./useNodegroupGeneralConfig";
import { useTranslation } from "react-i18next";
import {
  getNodegroupTemplate,
  getHadoopTemplate,
  getMySQLTemplate,
  getVIATemplate,
  getAIOpsTemplate,
  empty,
  getDruidTemplate,
  getGiteaTemplate,
  getNeo4jTemplate,
} from "@/components/instance/config/ServerToClientConverter";
import { useDruidConfig } from "./useDruidConfig";
import { useGiteaConfig } from "./useGiteaConfig";
import { useNeo4jConfig } from "./useNeo4jConfig";

export const useWizardColumnsConfig = ({
  tenant,
  formRef,
  onValuesChangeCallback,
  version,
  aZone,
  enableDruid,
  enableGitea,
  enableNeo4j,
  instanceType,
}) => {
  const { t } = useTranslation();
  const generalConfig = useGeneralConfig({ tenant, formRef, onValuesChangeCallback });
  const nodegroupConfig = useNodegroupConfig(enableDruid);
  const hadoopConfig = useHadoopConfig();
  const mySQLConfig = useMySQLConfig();
  const viaConfig = useVIAConfig();
  const aiopsConfig = useAIOpsConfig(version);
  const druidConfig = useDruidConfig();
  const giteaConfig = useGiteaConfig();
  const neo4jConfig = useNeo4jConfig();
  const poolConfig = usePoolConfig(aZone);
  const poolGeneral = usePoolGeneralConfig({
    formRef,
    onValuesChangeCallback,
    instanceType,
  });
  const nodegroupGeneral = useNodegroupGeneralConfig({
    formRef,
    onValuesChangeCallback,
  });

  const siloColumns = [
    generalConfig,
    nodegroupConfig,
    hadoopConfig,
    mySQLConfig,
    viaConfig,
    aiopsConfig,
  ];
  const siloTemplate = [
    empty,
    getNodegroupTemplate,
    getHadoopTemplate,
    getMySQLTemplate,
    getVIATemplate,
    getAIOpsTemplate,
  ];

  const siloSteps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.nodegroup", "Nodegroup"),
    t("instances.wizard.tab.hadoop", "Hadoop"),
    t("instances.wizard.tab.mysql", "MySQL"),
    t("instances.wizard.tab.via", "VIA"),
    t("instances.wizard.tab.aiops", "AIOps"),
  ].map((title) => ({ title }));

  if(enableDruid) {
    siloColumns.splice(4, 0, druidConfig);
    siloTemplate.splice(4, 0, getDruidTemplate);
    siloSteps.splice(4, 0, {"title": t("instances.wizard.tab.druid", "Druid")});
  }

  // appended, never spliced at a fixed index: the Druid block above splices, so an
  // index-based insert here would land differently depending on which ran first and
  // would break the columns/template/steps alignment the wizard relies on when it
  // indexes templateFun by the current step
  if(enableGitea) {
    siloColumns.push(giteaConfig);
    siloTemplate.push(getGiteaTemplate);
    siloSteps.push({"title": t("instances.wizard.tab.gitea", "Gitea")});
  }

  if(enableNeo4j) {
    siloColumns.push(neo4jConfig);
    siloTemplate.push(getNeo4jTemplate);
    siloSteps.push({"title": t("instances.wizard.tab.neo4j", "Neo4j")});
  }

  const poolColumns = [generalConfig, poolConfig, viaConfig, aiopsConfig];
  const poolSteps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.pool", "Pool"),
    t("instances.wizard.tab.via", "VIA"),
    t("instances.wizard.tab.aiops", "AIOps"),
  ].map((title) => ({ title }));
  const poolTemplate = [empty, empty, getVIATemplate, getAIOpsTemplate];

  const hadoopClusterColumns = [poolGeneral, hadoopConfig];
  const hadoopClusterSteps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.hadoop", "Hadoop"),
  ].map((title) => ({ title }));
  const hadoopClusterTemplate = [empty, getHadoopTemplate];

  const mysqlClusterColumns = [poolGeneral, mySQLConfig];
  const mysqlClusterSteps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.mysql", "MySQL"),
  ].map((title) => ({ title }));
  const mysqlClusterTemplate = [empty, getMySQLTemplate];

  const nodegroupClusterColumns = [nodegroupGeneral, nodegroupConfig];
  const nodegroupClusterSteps = [
    t("instances.wizard.tab.general", "General"),
    t("instances.wizard.tab.nodegroup", "Nodegroup"),
  ].map((title) => ({ title }));
  const nodegroupClusterTemplate = [empty, getNodegroupTemplate];

  const steps =
    instanceType === "silo"
      ? siloSteps
      : instanceType === "pool-infra"
      ? poolSteps
      : instanceType === "hadoop"
      ? hadoopClusterSteps
      : instanceType === "mysql"
      ? mysqlClusterSteps
      : nodegroupClusterSteps;
  const columns =
    instanceType === "silo"
      ? siloColumns
      : instanceType === "pool-infra"
      ? poolColumns
      : instanceType === "hadoop"
      ? hadoopClusterColumns
      : instanceType === "mysql"
      ? mysqlClusterColumns
      : nodegroupClusterColumns;
  const templateFun =
    instanceType === "silo"
      ? siloTemplate
      : instanceType === "pool-infra"
      ? poolTemplate
      : instanceType === "hadoop"
      ? hadoopClusterTemplate
      : instanceType === "mysql"
      ? mysqlClusterTemplate
      : nodegroupClusterTemplate;

  return {
    steps,
    columns,
    templateFun,
  };
};
